import type {
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import { DecoratorNode } from 'lexical'
import { type JSX } from 'react'

type HashtagMentionPayloadCommon = {
    id: string
    title: string
}

type HashtagMentionPayloadMeeting = HashtagMentionPayloadCommon & {
    type: 'Meeting'
    meeting: {
        id: string
        meetingStartDateUtc: string
        meetingEndDateUtc: string
        isAllDay: boolean
    }
}

type HashtagMentionPayloadSpace = HashtagMentionPayloadCommon & {
    type: 'team' | 'project' | 'committee' | 'board' | 'Group'
}

type HashtagMentionPayloadContentItem = HashtagMentionPayloadCommon & {
    type:
        | 'Action'
        | 'Decision'
        | 'Agenda'
        | 'Tag'
        | 'Poll'
        | 'Note'
        | 'Attachment'
        | 'MOM'
    meeting: {
        id: string
        meetingStartDateUtc: string
        meetingEndDateUtc: string
        isAllDay: boolean
    }
}

type HashtagMentionPayload =
    | HashtagMentionPayloadMeeting
    | HashtagMentionPayloadSpace
    | HashtagMentionPayloadContentItem

type SerializedHashtagMentionNode = Spread<
    {
        mention: HashtagMentionPayload
        type: 'hashtag_mention'
        version: 1
    },
    SerializedLexicalNode
>

class HashtagMentionNodeHeadless extends DecoratorNode<JSX.Element> {
    __mention: HashtagMentionPayload

    constructor(mention: HashtagMentionPayload, key?: NodeKey) {
        super(key)
        this.__mention = mention
    }

    static getType(): string {
        return 'hashtag_mention'
    }

    static clone(node: HashtagMentionNodeHeadless): HashtagMentionNodeHeadless {
        return $createHashtagMentionNodeHeadless(node.__mention, node.__key)
    }

    exportJSON(): SerializedHashtagMentionNode {
        return {
            mention: this.__mention,
            type: 'hashtag_mention',
            version: 1,
        }
    }

    static importJSON(
        serializedNode: SerializedHashtagMentionNode,
    ): HashtagMentionNodeHeadless {
        const node = $createHashtagMentionNodeHeadless(serializedNode.mention)
        return node
    }

    getTextContent(): string {
        if (this.__mention.type === 'Meeting') {
            return `#<${this.__mention.id}><${this.__mention.type}><${
                this.__mention.meeting.id
            }>${this.__mention.title.replace('\n', '\\n')}*`
        }

        if (
            this.__mention.type === 'team' ||
            this.__mention.type === 'project' ||
            this.__mention.type === 'committee' ||
            this.__mention.type === 'board' ||
            this.__mention.type === 'Group'
        ) {
            return `#<${this.__mention.id}><${this.__mention.type}><Empty Meeting>${this.__mention.title.replace('\n', '\\n')}*`
        }

        if (
            this.__mention.type === 'Action' ||
            this.__mention.type === 'Agenda' ||
            this.__mention.type === 'Attachment' ||
            this.__mention.type === 'Decision' ||
            this.__mention.type === 'MOM' ||
            this.__mention.type === 'Note' ||
            this.__mention.type === 'Poll' ||
            this.__mention.type === 'Tag'
        ) {
            return `#<${this.__mention.id}><${this.__mention.type}><${
                this.__mention.meeting.id
            }>${this.__mention.title.replace('\n', '\\n')}*`
        }

        return `#<${this.__mention.id}><${this.__mention.type}><Empty Meeting>${this.__mention.title.replace('\n', '\\n')}*`
    }
}

function $createHashtagMentionNodeHeadless(
    mention: HashtagMentionPayload,
    key?: NodeKey,
) {
    return new HashtagMentionNodeHeadless(mention, key)
}

function $isHashtagMentionNodeHeadless(
    node: HashtagMentionNodeHeadless | LexicalNode | null | undefined,
): node is HashtagMentionNodeHeadless {
    return node instanceof HashtagMentionNodeHeadless
}

export {
    type HashtagMentionPayload,
    type HashtagMentionPayloadMeeting,
    type HashtagMentionPayloadSpace,
    type HashtagMentionPayloadContentItem,
    type SerializedHashtagMentionNode,
    HashtagMentionNodeHeadless,
    $createHashtagMentionNodeHeadless,
    $isHashtagMentionNodeHeadless,
}
